import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Check, Sparkles, Sun, Moon, Monitor, Loader2 } from "lucide-react";
import NeuralLines from "../components/NeuralLines";
import { supabase } from "../../lib/supabase";
import useTheme from "../../components/aura/useTheme";

// ─── Static data ──────────────────────────────────────────────────────────────

const TARGET_LANGUAGES = [
  { code: "German", label: "German", flag: "🇩🇪" },
];

const NATIVE_LANGUAGES = [
  "English", "Hindi", "Spanish", "French", "German", "Mandarin",
  "Arabic", "Portuguese", "Russian", "Japanese", "Italian", "Other",
];

const LEVELS = [
  { id: "A1", label: "A1 · Just starting", body: "Greetings, simple sentences" },
  { id: "A2", label: "A2 · Everyday basics", body: "Familiar topics, present tense" },
  { id: "B1", label: "B1 · Conversational", body: "Past, future, opinions" },
  { id: "B2", label: "B2 · Independent", body: "Complex topics, debates" },
  { id: "C1", label: "C1 · Fluent", body: "Nuance, idioms, professional" },
  { id: "assess", label: "I don't know", body: "AURA will assess you in a 90-second chat" },
];

const GOALS = [
  { id: "relocation", label: "Relocate to a new country" },
  { id: "exam", label: "Pass a language exam" },
  { id: "career", label: "Land a job interview" },
  { id: "love", label: "Speak with someone I love" },
  { id: "travel", label: "Travel without translating" },
  { id: "culture", label: "Connect with the culture" },
  { id: "other", label: "Just curious — no pressure" },
];

const STEPS = ["Name", "Language", "Native", "Level", "Goal"];

// ─── Stepper ──────────────────────────────────────────────────────────────────

const Step = ({ active, label, idx }) => (
  <div className="flex items-center gap-2">
    <span className={`w-6 h-6 rounded-full grid place-items-center font-mono text-[10px] tracking-[0.18em] uppercase ${
      active === idx ? "bg-aura-cyan text-[#02121A]"
      : active > idx ? "bg-aura-cyan/15 text-aura-cyan"
      : "border border-panel/15 text-whisper-muted"
    }`}>
      {active > idx ? <Check size={11} /> : idx + 1}
    </span>
    <span className={`font-mono text-[10px] tracking-[0.22em] uppercase ${active >= idx ? "text-ink" : "text-whisper-muted"}`}>
      {label}
    </span>
  </div>
);

// ─── Theme toggle ─────────────────────────────────────────────────────────────

const ThemeToggle = () => {
  const { mode, setTheme } = useTheme();
  const options = [
    { id: "light", icon: Sun },
    { id: "system", icon: Monitor },
    { id: "dark", icon: Moon },
  ];
  return (
    <div className="flex items-center gap-1 rounded-full border border-panel/15 bg-panel/[0.03] p-1">
      {options.map(({ id, icon: Icon }) => (
        <button
          key={id}
          onClick={() => setTheme(id)}
          aria-label={`${id} mode`}
          className={`w-7 h-7 rounded-full grid place-items-center transition-colors ${
            mode === id ? "bg-aura-cyan/15 text-aura-cyan" : "text-whisper-muted hover:text-ink"
          }`}
        >
          <Icon size={13} />
        </button>
      ))}
    </div>
  );
};

// ─── Onboarding ───────────────────────────────────────────────────────────────

const Onboarding = () => {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [firstName, setFirstName] = useState("");
  const [targetLang, setTargetLang] = useState(null);
  const [nativeLang, setNativeLang] = useState(null);
  const [level, setLevel] = useState(null);
  const [goal, setGoal] = useState(null);

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => { setStep((s) => Math.max(0, s - 1)); setError(""); };

  // Per-step validation — all mandatory
  const canProceed = () => {
    if (step === 0) return firstName.trim().length >= 1;
    if (step === 1) return !!targetLang;
    if (step === 2) return !!nativeLang;
    if (step === 3) return !!level;
    if (step === 4) return !!goal;
    return false;
  };

  // Save everything to Supabase
  const handleFinish = async () => {
    setSaving(true);
    setError("");
    try {
      const { data: authData } = await supabase.auth.getUser();
      const uid = authData?.user?.id;
      if (!uid) throw new Error("Not logged in");

      const needsAssessment = level === "assess";
      const cefrLevel = needsAssessment ? "A1" : level;
      const placementStatus = needsAssessment ? "needs_assessment" : "placed";

      // Upsert user profile
      const { error: profileErr } = await supabase.from("users").upsert({
        id: uid,
        display_name: firstName.trim(),
        target_language: targetLang,
        native_language: nativeLang,
        cefr_level: cefrLevel,
        claimed_cefr_level: needsAssessment ? null : cefrLevel,
        effective_cefr_level: cefrLevel,
        placement_status: placementStatus,
        last_assessed_at: null,
        assessment_completed_at: null,
        learning_goal: goal,
        streak: 0,
      }, { onConflict: "id" });

      if (profileErr) throw profileErr;

      // Seed memory document
      await supabase.from("memory_documents").upsert({
        user_id: uid,
        memory_json: {
          cefr_level: cefrLevel,
          claimed_cefr_level: needsAssessment ? null : cefrLevel,
          effective_cefr_level: cefrLevel,
          placement_status: placementStatus,
          strengths: [],
          working_on: [],
          next_focus: level === "assess"
            ? `Assess ${firstName}'s ${targetLang} level from scratch in the first session`
            : level === "A1"
              ? `Start ${targetLang} basics for goal: ${goal}`
              : `Verify ${firstName}'s claimed ${cefrLevel} proficiency through conversational assessment`,
          failed_vocabulary: [],
          mastered_vocabulary: [],
          personal_context: [
            { fact: `Name: ${firstName}`, first_mentioned: new Date().toISOString() },
            { fact: `Native language: ${nativeLang}`, first_mentioned: new Date().toISOString() },
            { fact: `Goal: ${goal}`, first_mentioned: new Date().toISOString() },
          ],
        },
        updated_at: new Date().toISOString(),
      }, { onConflict: "user_id" });

      // Profile saved — navigate into the dashboard.
      // target_language is now in the users table and will be read
      // from there on every session start. No localStorage needed.

      nav("/dashboard", { replace: true });
    } catch (err) {
      console.error("Onboarding save error:", err);
      setError("Could not save your profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-base text-ink overflow-hidden" data-testid="page-onboarding">
      <NeuralLines />
      <div aria-hidden className="pointer-events-none fixed inset-0 z-[1]" style={{ background: "linear-gradient(180deg, rgb(var(--bg-rgb) / 0.7) 0%, rgb(var(--bg-rgb) / 0.92) 100%)" }} />

      <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-6 py-10">
        {/* Header — NO skip button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-2.5 h-2.5 rounded-full bg-aura-cyan shadow-[0_0_12px_rgb(var(--accent-cyan-rgb)/0.9)]" />
            <span className="font-display text-[20px] tracking-[-0.02em] text-ink font-light">
              AURA<span className="text-aura-cyan">.</span>
            </span>
          </div>
          <ThemeToggle />
        </div>

        {/* Stepper */}
        <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-3 overflow-x-auto pb-1" data-testid="onboarding-stepper">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <Step active={step} idx={i} label={label} />
              {i < STEPS.length - 1 && <span className="hidden sm:inline-block w-6 h-px bg-panel/15" />}
            </React.Fragment>
          ))}
        </div>

        {/* Card */}
        <div className="mt-10 rounded-3xl glass edge-glow p-6 sm:p-10 min-h-[460px] relative overflow-hidden">
          <div aria-hidden className="absolute -top-32 -right-24 w-[360px] h-[360px] rounded-full bg-aura-cyan/15 blur-[140px]" />

          <AnimatePresence mode="wait">

            {/* Step 0 — Name */}
            {step === 0 && (
              <motion.div key="name" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }} className="relative">
                <div className="section-label">Step 1 of {STEPS.length}</div>
                <h2 className="mt-3 font-display text-[28px] sm:text-[36px] font-light tracking-tight text-ink leading-tight">
                  What should AURA <em>call you</em>?
                </h2>
                <p className="mt-3 text-whisper text-[14.5px]">She'll use your name throughout every session.</p>
                <div className="mt-8">
                  <input
                    autoFocus
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && canProceed()) next(); }}
                    placeholder="Your first name"
                    aria-label="Your first name"
                    className="w-full max-w-sm bg-panel/[0.04] border border-panel/15 rounded-2xl px-4 py-3.5 text-[18px] text-ink placeholder:text-whisper-muted focus:outline-none focus:border-aura-cyan/50 transition-colors"
                    data-testid="onboarding-name"
                  />
                </div>
              </motion.div>
            )}

            {/* Step 1 — Target language */}
            {step === 1 && (
              <motion.div key="lang" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }} className="relative">
                <div className="section-label">Step 2 of {STEPS.length}</div>
                <h2 className="mt-3 font-display text-[28px] sm:text-[36px] font-light tracking-tight text-ink leading-tight">
                  Which language do you want to <em>own</em>?
                </h2>
                <p className="mt-3 text-whisper text-[14.5px]">This becomes AURA's primary teaching language. You can add more later.</p>
                <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="onboarding-languages">
                  {TARGET_LANGUAGES.map((l) => (
                    <button key={l.code} onClick={() => setTargetLang(l.code)}
                      className={`hairline rounded-2xl p-4 flex flex-col items-center gap-2 transition-colors ${
                        targetLang === l.code ? "border-aura-cyan/50 bg-aura-cyan/5" : "hover:border-aura-cyan/30"
                      }`}
                      data-testid={`onboarding-lang-${l.code.toLowerCase()}`}
                    >
                      <span className="text-[28px]">{l.flag}</span>
                      <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase">{l.label}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 2 — Native language */}
            {step === 2 && (
              <motion.div key="native" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }} className="relative">
                <div className="section-label">Step 3 of {STEPS.length}</div>
                <h2 className="mt-3 font-display text-[28px] sm:text-[36px] font-light tracking-tight text-ink leading-tight">
                  What language do you <em>think</em> in?
                </h2>
                <p className="mt-3 text-whisper text-[14.5px]">AURA adjusts her explanations and uses your native language when clarity matters.</p>
                <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="onboarding-native">
                  {NATIVE_LANGUAGES.map((l) => (
                    <button key={l} onClick={() => setNativeLang(l)}
                      className={`hairline rounded-2xl p-4 text-left transition-colors ${
                        nativeLang === l ? "border-aura-cyan/50 bg-aura-cyan/5" : "hover:border-aura-cyan/30"
                      }`}
                      data-testid={`onboarding-native-${l.toLowerCase()}`}
                    >
                      <div className="font-display text-[16px] text-ink">{l}</div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 3 — Level */}
            {step === 3 && (
              <motion.div key="level" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }} className="relative">
                <div className="section-label">Step 4 of {STEPS.length}</div>
                <h2 className="mt-3 font-display text-[28px] sm:text-[36px] font-light tracking-tight text-ink leading-tight">
                  Where do you stand <em>today</em>?
                </h2>
                <p className="mt-3 text-whisper text-[14.5px]">A starting point — AURA will calibrate the moment you speak.</p>
                <div className="mt-7 grid sm:grid-cols-2 gap-3" data-testid="onboarding-levels">
                  {LEVELS.map((l) => (
                    <button key={l.id} onClick={() => setLevel(l.id)}
                      className={`hairline rounded-2xl p-4 text-left transition-colors ${
                        level === l.id ? "border-aura-cyan/50 bg-aura-cyan/5" : "hover:border-aura-cyan/30"
                      }`}
                      data-testid={`onboarding-level-${l.id}`}
                    >
                      <div className="font-display text-[16px] text-ink">{l.label}</div>
                      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted mt-1">{l.body}</div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 4 — Goal */}
            {step === 4 && (
              <motion.div key="goal" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }} className="relative">
                <div className="section-label">Step 5 of {STEPS.length}</div>
                <h2 className="mt-3 font-display text-[28px] sm:text-[36px] font-light tracking-tight text-ink leading-tight">
                  What are you <em>going</em> to do with it?
                </h2>
                <p className="mt-3 text-whisper text-[14.5px]">Your goal locks the curriculum to your life. You can change it any time in settings.</p>
                <div className="mt-7 grid sm:grid-cols-2 gap-3" data-testid="onboarding-goals">
                  {GOALS.map((g) => (
                    <button key={g.id} onClick={() => setGoal(g.id)}
                      className={`hairline rounded-2xl p-4 text-left transition-colors ${
                        goal === g.id ? "border-aura-cyan/50 bg-aura-cyan/5" : "hover:border-aura-cyan/30"
                      }`}
                      data-testid={`onboarding-goal-${g.id}`}
                    >
                      <div className="font-display text-[16px] text-ink">{g.label}</div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}



          </AnimatePresence>

          {/* Error */}
          {error && (
            <div className="mt-4 rounded-xl border border-red-400/30 bg-red-400/[0.07] px-4 py-3 text-[13px] text-red-300">
              {error}
            </div>
          )}
        </div>

        {/* Navigation — NO skip */}
        <div className="mt-6 flex items-center justify-between">
          <button onClick={back} disabled={step === 0} className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed" data-testid="onboarding-back">
            <ChevronLeft size={14} /> back
          </button>
          {step < STEPS.length - 1 ? (
            <button
              onClick={next}
              disabled={!canProceed()}
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="onboarding-next"
              title={!canProceed() ? "Please make a selection to continue" : ""}
            >
              continue <ChevronRight size={14} />
            </button>
          ) : (
            <button
              onClick={handleFinish}
              disabled={!canProceed() || saving}
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="onboarding-finish"
            >
              {saving ? <><Loader2 size={14} className="animate-spin" /> saving…</> : <>enter dashboard <ChevronRight size={14} /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
